import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export function validateEmail(email: string): boolean {
  return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
    email
  );
}

export function validateCharacters(
  stringToValidate: string,
  charactersExpectedToFind: string[],
  allRequired = false
): boolean {
  if (!allRequired) {
    return charactersExpectedToFind.some((char) =>
      stringToValidate.includes(char)
    );
  }

  let hasCharacters = true;
  charactersExpectedToFind.forEach((char) => {
    if (!hasCharacters) {
      return;
    }
    hasCharacters = stringToValidate.includes(char);
  });
  return hasCharacters;
}

export function validateMobile(mobile: string): boolean {
  return /^04[0-9]{8}$/i.test(mobile);
}

export function validateAustralianUserMobile(mobile: string): boolean {
  return /^(04|\+614)[0-9]{8}$/i.test(mobile);
}

export type AbnValidationResult = {
  isValid: boolean;
  error?: 'invalid-length' | 'invalid-checksum';
};

export const normalizeAbn = (abn?: string | null): string =>
  (abn ?? '').replace(/\D/g, '');

export function validateAbn(abn: string): AbnValidationResult {
  const cleanABN = normalizeAbn(abn);

  if (!/^\d{11}$/.test(cleanABN)) {
    return { isValid: false, error: 'invalid-length' };
  }

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const digits = cleanABN.split('').map(Number);

  digits[0] = digits[0] - 1;

  const sum = digits.reduce(
    (acc, digit, index) => acc + digit * weights[index],
    0
  );

  const isValid = sum % 89 === 0;

  return isValid
    ? { isValid: true }
    : { isValid: false, error: 'invalid-checksum' };
}

export function formatAbn(abn: string): string {
  const cleanABN = abn.replace(/\D/g, '');

  if (cleanABN.length <= 2) {
    return cleanABN;
  }
  if (cleanABN.length <= 5) {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2)}`;
  }
  if (cleanABN.length <= 8) {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2, 5)} ${cleanABN.slice(5)}`;
  }
  return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2, 5)} ${cleanABN.slice(5, 8)} ${cleanABN.slice(8, 11)}`;
}

export function validateProtocol(url: string): boolean {
  return /^(http(s)?:\/\/)/gi.test(url);
}

export function validateUrl(url: string): boolean {
  return /^(http(s)?:\/\/)[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,12}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi.test(
    url
  );
}

export function validateUrlWithOptionalProtocol(url: string): boolean {
  return /^(https?:\/\/)?[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,12}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?$/i.test(
    url
  );
}

export function isFormikValid(formik: {
  touched: unknown;
  errors: unknown;
}): boolean {
  return (
    Object.keys(formik.touched).length && !Object.keys(formik.errors).length
  );
}

export function formikHasErrors(formik: { errors: unknown }): boolean {
  return !Object.keys(formik.errors).length;
}

export function hasDateExpired(expiryDate: Date = new Date()): boolean {
  return new Date() >= expiryDate;
}

export function renderFieldError(
  touched?: boolean,
  error?: string,
  submitAttempted?: boolean
): React.ReactNode {
  if (!(touched || submitAttempted) || !error) {
    return null;
  }

  return (
    <span className="errorRow">
      <ErrorOutlineIcon className="errorIcon" aria-hidden />
      {error}
    </span>
  );
}
